import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Surface, ErrorState, TableLoadingOverlay } from '@/components'
import { ContentPlaceholder } from '@/components/molecules'
import { IntervalDataChart } from '../IntervalDataChart/IntervalDataChart'
import {
    GranularityType,
    IntervalReportSettings,
    useActiveManagementApi,
} from '@/hooks'
import { IntervalMetersAlert } from './IntervalMetersAlert'
import { Granularity } from '@/components/molecules'
import { useIntervalSummaryData } from '../IntervalSummary/useIntervalSummaryData'
import { IntervalSummaryCard } from '../IntervalSummary/IntervalSummaryCard'
import { DataSummary } from '@/components/common/LineChartLegends/DataSummary'
import { ViewType } from '@/stores/useViewAsToggle/useViewAsToggle'
import { IntervalTableContainer } from '../IntervalTableContainer/IntervalTableContainer'
import { useActiveManagementFiltersGetParams } from '../../hooks/useActiveManagementFiltersGetParams'
import { ActiveManagementFiltersValues } from '@/utils/constants'
import { Toggle, InfoText } from '@/components/atoms'

type IntervalViewProps = {
    activeFilters: ActiveManagementFiltersValues
    reportSettings: IntervalReportSettings
    setReportSettings: (settings: Partial<IntervalReportSettings>) => void
    activeView: ViewType
}

export const IntervalView: React.FC<IntervalViewProps> = ({
    reportSettings,
    activeFilters,
    setReportSettings,
    activeView,
}) => {
    const { t } = useTranslation('translations')
    const showHighlights = reportSettings.showHighlights ?? true

    const { useIntervalDataStreamedQuery } = useActiveManagementApi()
    const filterParams = useActiveManagementFiltersGetParams(
        activeFilters,
        reportSettings
    )

    const {
        data: streamedIntervalData,
        isError,
        isLoading,
        isFetching,
        isFetched,
    } = useIntervalDataStreamedQuery(filterParams, { reportSettings })

    const data = useMemo(
        () => streamedIntervalData?.data ?? [],
        [streamedIntervalData?.data]
    )
    const highlights = streamedIntervalData?.highlights

    const summaryData = useIntervalSummaryData(
        activeFilters,
        reportSettings,
        data
    )

    const hasData = Array.isArray(data) && data.length > 0

    const loadingMessage =
        activeView === 'chart'
            ? t('analytics.common.report_chart_loading')
            : t('analytics.common.report_table_loading')
    const emptyStateDescription = t(
        'analytics.common.report_empty_state.description'
    )

    if (isError) {
        return <ErrorState description={t('analytics.common.report_error')} />
    }

    if (isFetched && !hasData && !isLoading && !isFetching) {
        return (
            <ContentPlaceholder
                title={t('analytics.common.report_empty_state.title')}
                description={emptyStateDescription}
                illustrationSrc="/assets/empty-state-illustration.svg"
                name="empty-state"
            />
        )
    }

    const handleGranularityChange = (granularityType: GranularityType) => {
        setReportSettings({ granularity: granularityType })
    }

    const handleHighlightsChange = (checked: boolean) => {
        setReportSettings({ showHighlights: checked })
    }

    const handleBrushManualChange = () => {
        setReportSettings({ granularity: undefined })
    }

    return (
        <div className="relative h-full">
            <div className="flex h-full w-full flex-col">
                {reportSettings.dataGrouping !== 'aggregate' && (
                    <div className="flex-shrink-0">
                        <IntervalMetersAlert
                            activeFilters={activeFilters}
                            display={reportSettings.display}
                        />
                    </div>
                )}
                {activeView === 'chart' ? (
                    <Surface className="flex h-full min-w-minWidgetWidth flex-1 flex-col gap-4">
                        <div className="flex items-start justify-between gap-4 pb-8">
                            <div className="flex flex-wrap items-center gap-6">
                                <Granularity
                                    selectedGranularity={
                                        reportSettings.granularity
                                    }
                                    onSelect={handleGranularityChange}
                                />
                                <div className="flex items-center gap-2">
                                    <InfoText
                                        className="font-medium text-subtle"
                                        text={t(
                                            'active_management.interval.highlights_label'
                                        )}
                                        icon={null}
                                    />
                                    <Toggle
                                        checked={showHighlights}
                                        onChange={handleHighlightsChange}
                                    />
                                </div>
                            </div>
                            <DataSummary
                                showSummaryDropdown={true}
                                label={t(
                                    'active_management.interval.summary.button'
                                )}
                                disabled={!hasData}
                                buttonName="summary-toggle"
                                renderDropdownContent={({
                                    onClose,
                                    onMouseEnter,
                                    onMouseLeave,
                                }) => (
                                    <IntervalSummaryCard
                                        summary={summaryData}
                                        onClose={onClose}
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                    />
                                )}
                            />
                        </div>
                        <div className="relative flex-1">
                            <IntervalDataChart
                                data={data}
                                reportSettings={reportSettings}
                                activeFilters={activeFilters}
                                granularity={reportSettings.granularity}
                                onBrushManualChange={handleBrushManualChange}
                                highlights={highlights}
                                showHighlights={showHighlights}
                            />
                        </div>
                    </Surface>
                ) : (
                    <div className="relative min-h-widgetHeight flex-1">
                        <IntervalTableContainer
                            activeFilters={activeFilters}
                            reportSettings={reportSettings}
                            data={data}
                        />
                    </div>
                )}
            </div>
            <TableLoadingOverlay
                show={(!data.length && !isFetched) || isFetching}
                title={loadingMessage}
            />
        </div>
    )
}
